/* eslint-disable array-bracket-spacing */
import React, { useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { generatePath, useNavigate } from 'react-router-dom';
import {
    ApartmentOutlined,
    AuditOutlined,
    DesktopOutlined,
    EditOutlined,
    GlobalOutlined,
    LoginOutlined,
    MobileOutlined,
} from '@ant-design/icons';
import BaseModal from '@components/common/form/BaseModal';
// import styles from './index.module.scss';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import {
    ALL_REQUEST,
    appAccount,
    AppConstants,
    DATE_FORMAT_DISPLAY,
    DEFAULT_TABLE_ITEM_SIZE_LARGE,
    grantTypeShopAccount,
    P_CODE_GRANT_TYPE_SHOP_ACCOUNT,
    STATUS_LOCK,
} from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import { hasTabletRequestOptions, statusOptions } from '@constants/masterData';
import useAuth from '@hooks/useAuth';
import useFetch from '@hooks/useFetch';
import useListModal from '@hooks/useListModal';
import useTranslate from '@hooks/useTranslate';
import { BaseTable, BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import routes from '@routes';
import { showErrorMessage } from '@services/notifyService';
import { getCacheAccessToken } from '@services/userService';
import { convertUtcToLocalTime, getDefaultSettings } from '@utils';
import { Button } from 'antd';

import RestaurantForm from './RestaurantForm';
import useRestaurantFormModal from './useRestaurantFormModal';

import styles from '../kitplayShopAccount/index.module.scss';


const message = defineMessages({
    objectName: 'restaurants',
    breadcrumb: 'Website Order',
    loginFail: 'Login failed!',
});

const CREATE_FORM_ID = 'form-restaurant-create';

const RestaurantListPage = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const statusValues = translate.formatKeys(statusOptions, ['label']);
    const hasTabletRequestValues = translate.formatKeys(hasTabletRequestOptions, ['label']);

    const { loading: updateRestaurantPinLoading } = useFetch(apiConfig.restaurant.update);

    const accessToken = getCacheAccessToken();
    const { pcodes } = useAuth();

    const base64Credentials = Buffer.from(`${appAccount.APP_USERNAME}:${appAccount.APP_PASSWORD}`).toString('base64');
    const { execute, loading: loadingLogin } = useFetch({
        ...apiConfig.account.loginBasic,
        authorization: `Basic ${base64Credentials}`,
    });

    const handleLogin = (id) => {
        execute({
            data: {
                token: accessToken,
                shopAccountId: id,
                grant_type: grantTypeShopAccount,
            },
            onCompleted: (res) => {
                const cmsUrl = AppConstants.cmsUrl || `/`;
                const targetUrl = cmsUrl + `verify` + `?accessToken=${res?.access_token}`;
                window.open(targetUrl, '_blank', 'noopener,noreferrer');
            },
            onError: () => showErrorMessage(translate.formatMessage(message.loginFail), translate),
        });
    };


    const {
        data,
        mixinFuncs,
        queryFilter,
        loading,
        pagination,
        setQueryParams,
        detail,
        openModal,
        onCancelModal,
        setIsChangedFormValues,
        onSave,
        titleModal,
    } = useListModal({
        apiConfig: {
            getList: apiConfig.restaurant.getList,
            getById: apiConfig.restaurant.getById,
            create: apiConfig.restaurant.create,
            update: apiConfig.restaurant.update,
            delete: apiConfig.restaurant.delete,
            changeStatus: apiConfig.restaurant.update,
        },
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE_LARGE,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            const originalPrepareGetListParams = funcs.prepareGetListParams;
            funcs.prepareGetListParams = (params) => {
                const preparedParams = originalPrepareGetListParams(params);
                return {
                    ...preparedParams,
                    serviceFilter: 'WEBSITE_ORDER',
                };
            };
            funcs.additionalActionColumnButtons = () => ({
                login: (record) => (
                    <BaseTooltip title={translate.formatMessage(commonMessage.management)}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            disabled={loadingLogin || !(pcodes?.includes(P_CODE_GRANT_TYPE_SHOP_ACCOUNT)) || record?.tenantId === null || record?.tenantId === undefined}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleLogin(record?.accountDto?.id);
                            }}
                        >
                            <LoginOutlined />
                        </Button>
                    </BaseTooltip>
                ),
                edit: (record) => (
                    <Button
                        type="link"
                        style={{ padding: 0 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            openRestaurantModal(record);
                        }}
                    >
                        <EditOutlined />
                    </Button>
                ),
                webPortal: ({ id, status, tenantId }) =>
                    status !== STATUS_LOCK && (
                        <BaseTooltip title={translate.formatMessage(commonMessage.webPortal)}>
                            <Button
                                type="link"
                                style={{ padding: 0 }}
                                href={`${AppConstants.webPortal}${id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                disabled={tenantId === null || tenantId === undefined}
                            >
                                <GlobalOutlined />
                            </Button>
                        </BaseTooltip>
                    ),

                devices: ({ id, restaurantName }) => (
                    <BaseTooltip title={translate.formatMessage(commonMessage.devices)}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={() => {
                                navigate(
                                    routes.resDeviceTabsRestaurantListPage.path +
                                    `?tab=devices&restaurantId=${id}&restaurantName=${restaurantName}`,
                                );
                            }}
                        >
                            <MobileOutlined />
                        </Button>
                    </BaseTooltip>
                ),
                updateDatabase: (record) => (
                    <BaseTooltip title={translate.formatMessage(commonMessage.updateDatabase)}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                openDatabaseModal(record);
                            }}
                        >
                            <ApartmentOutlined />
                        </Button>
                    </BaseTooltip>
                ),
                thirdParty: ({ id, restaurantName }) => (
                    <BaseTooltip title={'Third party'}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={() => {
                                navigate(
                                    routes.thirdPartyListPage.path +
                                    `?restaurantId=${id}&restaurantName=${restaurantName}`,
                                );
                            }}
                        >
                            <AuditOutlined />
                        </Button>
                    </BaseTooltip>
                ),
                tabletRequest: ({ id, restaurantName }) => (
                    <BaseTooltip title={translate.formatMessage(commonMessage.tabletRequest)}>
                        <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(
                                    `${generatePath(
                                        routes.tabletRequestRestaurantListPage.path,
                                    )}?restaurantId=${id}&restaurantName=${restaurantName}`,
                                );
                            }}
                        >
                            <DesktopOutlined />
                        </Button>
                    </BaseTooltip>
                ),
            });
        },
    });

    const { openRestaurantModal, openDatabaseModal, renderModal } = useRestaurantFormModal({
        onReloadList: () => mixinFuncs.getList(),
    });

    const handleCreateForm = (values) => {
        onSave({
            ...values,
            settings: getDefaultSettings(),
        });
    };

    const columns = [
        // {
        //     title: 'Logo',
        //     dataIndex: 'logoPath',
        //     align: 'left',
        //     width: 50,
        //     render: (logoPath) => (
        //         <ImageCol path={logoPath ? `${AppConstants.contentRootUrl}${logoPath}` : null} className="img-logo" />
        //     ),
        // },
        {
            title: <FormattedMessage defaultMessage="Restaurant Name" />,
            dataIndex: 'restaurantName',
            // render: (text) => {
            //     return (
            //         <div
            //             style={{ cursor: 'pointer', color: '#4096ff' }}
            //         >
            //             {text}
            //         </div>
            //     );
            // },
        },
        {
            title: <FormattedMessage defaultMessage="Address" />,
            dataIndex: ['address'],
        },
        // {
        //     title: <FormattedMessage defaultMessage="ZIP code" />,
        //     width: 30,
        //     dataIndex: ['zipCode'],
        // },
        {
            title: <FormattedMessage defaultMessage="Expiration date" />,
            width: 180,
            dataIndex: 'expiredDate',
            align: 'right',
            render: (expiredDate) => {
                const expiredDateLocal = convertUtcToLocalTime(expiredDate, DATE_FORMAT_DISPLAY, DATE_FORMAT_DISPLAY);
                return <div>{expiredDateLocal}</div>;
            },
        },
        // {
        //     title: <FormattedMessage defaultMessage="Extend Date" />,
        //     width: 120,
        //     dataIndex: 'extendDate',
        //     render: (extendDate) => {
        //         const extendDateLocal = convertUtcToLocalTime(extendDate, DATE_FORMAT_DISPLAY, DATE_FORMAT_DISPLAY);
        //         return <div>{extendDateLocal}</div>;
        //     },
        // },
        mixinFuncs.renderStatusColumn({ width: '120px' }),
        mixinFuncs.renderActionColumn(
            {
                login: true,
                webPortal: true,
            },
            { width: '120px', className: styles['action-column'] },
        ),
    ];

    const searchFields = [
        {
            key: 'restaurantName',
            placeholder: translate.formatMessage(commonMessage.restaurantName),
        },
        {
            key: 'status',
            placeholder: translate.formatMessage(commonMessage.status),
            type: FieldTypes.SELECT,
            options: statusValues,
        },
        // {
        //     key: 'hasTabletRequest',
        //     placeholder: translate.formatMessage(commonMessage.hasTabletRequest),
        //     type: FieldTypes.SELECT,
        //     options: hasTabletRequestValues,
        // },
    ];

    useEffect(() => {
        if (!queryFilter.hasTabletRequest) {
            setQueryParams({
                ...queryFilter,
                hasTabletRequest: ALL_REQUEST,
            });
        }
    }, []);

    return (
        <PageWrapper routes={[{ breadcrumbName: translate.formatMessage(message.breadcrumb) }]}>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({
                    fields: searchFields,
                    initialValues: {
                        ...queryFilter,
                        hasTabletRequest: queryFilter.hasTabletRequest || ALL_REQUEST,
                    },
                    onReset: () => {
                        setQueryParams({
                            hasTabletRequest: ALL_REQUEST,
                        });
                    },
                })}
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        loading={loading || updateRestaurantPinLoading}
                        pagination={pagination}
                        rowKey="id"
                    // onRow={(record) => ({
                    //     onClick: (e) => {
                    //         e.stopPropagation();
                    //         navigate(
                    //             routes.resDeviceTabsRestaurantListPage.path +
                    //                 `?tab=devices&restaurantId=${record?.id}&restaurantName=${record?.restaurantName}`,
                    //         );
                    //     },
                    // })}
                    />
                }
            />
            {renderModal()}
            {openModal && !detail && (
                <BaseModal
                    isModalVisible={openModal}
                    isEditing={false}
                    handleCancel={onCancelModal}
                    title={titleModal}
                    width="800px"
                    noBodyPadding={true}
                    customFooter={
                        null
                    }
                >
                    <RestaurantForm
                        formId={CREATE_FORM_ID}
                        dataDetail={{}}
                        isEditing={false}
                        actions={null}
                        onSubmit={handleCreateForm}
                        setIsChangedFormValues={setIsChangedFormValues}
                    />
                </BaseModal>
            )}
        </PageWrapper>
    );
};

export default RestaurantListPage;
